/**
 * useHerald — API layer for the floating Herald button/panel.
 *
 * Provides:
 *   • Unread count polling (every 30s when auth'd)
 *   • compose() / send() (org-member only — surfaced as errors otherwise)
 *   • searchMentionables() debounced helper
 *   • listNotifications() / openNotification() / markRead() / markAllRead()
 *
 * Cluster D.3.6 (Feb 2026): optional `scopeTeamId` argument. When the
 * caller (HeraldPanel) hands a non-null team_id, the BE calls add
 * `?team_id={scopeTeamId}` to the team-aware surfaces
 * (`/api/herald/notifications`, `/api/herald/notifications/unread-count`,
 * `/api/org/mentionables`). When null/undefined, no team filter is
 * sent — today's behaviour preserved. The hook itself is unaware
 * of localStorage; the toggle source-of-truth lives in HeraldPanel.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API = getBackendBaseUrl();
const POLL_INTERVAL_MS = 30_000;

const _bearer = (token) => (token ? { Authorization: `Bearer ${token}` } : {});
const _withTeam = (path, scopeTeamId) => {
  if (!scopeTeamId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}team_id=${encodeURIComponent(scopeTeamId)}`;
};

async function _readJson(r) {
  try { return await r.clone().json(); } catch { try { return await r.json(); } catch { return null; } }
}
async function _readText(r) {
  try { return await r.clone().text(); } catch { return r.statusText || `HTTP ${r.status}`; }
}

export function useHerald(scopeTeamId = null) {
  const { session } = useAuth();
  const token = session?.access_token;

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);

  // ── Unread count polling ───────────────────────────────────────
  const refreshUnread = useCallback(async () => {
    if (!token) { setUnreadCount(0); return; }
    try {
      const r = await fetch(
        _withTeam(`${API}/api/herald/notifications/unread-count`, scopeTeamId),
        { headers: _bearer(token) },
      );
      if (!r.ok) return;
      const data = await _readJson(r);
      setUnreadCount(Number(data?.count ?? 0));
    } catch { /* network blip — leave stale count */ }
  }, [token, scopeTeamId]);

  useEffect(() => {
    if (!token) return undefined;
    refreshUnread();
    const id = setInterval(refreshUnread, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [token, refreshUnread]);

  // ── Inbox ──────────────────────────────────────────────────────
  const refreshInbox = useCallback(async () => {
    if (!token) return;
    setLoadingInbox(true);
    try {
      const r = await fetch(
        _withTeam(`${API}/api/herald/notifications?limit=30`, scopeTeamId),
        { headers: _bearer(token) },
      );
      if (!r.ok) { setNotifications([]); return; }
      const data = await _readJson(r);
      setNotifications(Array.isArray(data) ? data : []);
    } finally {
      setLoadingInbox(false);
    }
  }, [token, scopeTeamId]);

  const openNotification = useCallback(async (id) => {
    if (!token) return null;
    const r = await fetch(`${API}/api/herald/notifications/${encodeURIComponent(id)}`, {
      headers: _bearer(token),
    });
    if (!r.ok) throw new Error(await _readText(r));
    const detail = await _readJson(r);
    // Local state: that row is now read.
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    refreshUnread();
    return detail;
  }, [token, refreshUnread]);

  const markRead = useCallback(async (ids) => {
    if (!token || !ids || ids.length === 0) return;
    await fetch(`${API}/api/herald/notifications/read`, {
      method: 'PATCH',
      headers: { ..._bearer(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ notification_ids: ids }),
    });
    setNotifications((prev) =>
      prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n))
    );
    refreshUnread();
  }, [token, refreshUnread]);

  const markAllRead = useCallback(async () => {
    if (!token) return;
    await fetch(`${API}/api/herald/notifications/read-all`, {
      method: 'POST',
      headers: _bearer(token),
    });
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    refreshUnread();
  }, [token, refreshUnread]);

  // ── Compose + Send ─────────────────────────────────────────────
  const compose = useCallback(async ({ rawNote, mentions, context }) => {
    const r = await fetch(`${API}/api/herald/compose`, {
      method: 'POST',
      headers: { ..._bearer(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        raw_note: rawNote,
        recipient_mentions: mentions,
        context,
      }),
    });
    if (!r.ok) throw new Error(await _readText(r));
    return await _readJson(r);
  }, [token]);

  const send = useCallback(async ({ rawNote, subject, body, mentions, context }) => {
    const r = await fetch(`${API}/api/herald/send`, {
      method: 'POST',
      headers: { ..._bearer(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        raw_note: rawNote,
        composed_subject: subject,
        composed_body: body,
        recipient_mentions: mentions,
        context,
      }),
    });
    if (!r.ok) throw new Error(await _readText(r));
    refreshUnread();
    return await _readJson(r);
  }, [token, refreshUnread]);

  // ── Mentionables (debounced manually by callers) ───────────────
  const lastQueryRef = useRef('');
  const searchMentionables = useCallback(async (q) => {
    if (!token) return [];
    lastQueryRef.current = q;
    const r = await fetch(
      _withTeam(`${API}/api/org/mentionables?q=${encodeURIComponent(q || '')}`, scopeTeamId),
      { headers: _bearer(token) },
    );
    if (!r.ok) return [];
    if (lastQueryRef.current !== q) return null; // stale
    return (await _readJson(r)) || [];
  }, [token, scopeTeamId]);

  return {
    unreadCount, refreshUnread,
    notifications, loadingInbox, refreshInbox,
    openNotification, markRead, markAllRead,
    compose, send,
    searchMentionables,
  };
}
